<template>
  <div
    class="pick-item hover:brightness-120"
    :class="{
      'pick-item--packed': isPacked,
      'pick-item--missing': item.missing,
      'pick-item--partial': item.partial,
    }"
    @click="onRowClick"
  >
    <!-- Status stripe -->
    <div class="pick-item__stripe" />

    <div class="pick-item__body">
      <!-- Left: name + type badge -->
      <div class="pick-item__main">
        <div class="pick-item__name">{{ item.item?.name ?? item.asset_tag ?? item.serial_number ?? $t('picking.unknownItem') }}</div>
        <div class="pick-item__meta">
          <span class="badge" :class="typeBadgeClass">{{ typeName }}</span>
          <span v-if="checkoutStatusBadge" class="badge" :class="checkoutStatusBadge.cls">{{ checkoutStatusBadge.label }}</span>
          <span v-if="item.item?.item_type === 3 || item.item?.item_type === 4 || item.unresolved || (item.item?.item_type === 2 && item.planned_quantity > 1)" class="pick-item__qty-label">
            {{ item.packed_quantity ?? 0 }} / {{ item.planned_quantity }} {{ $t('ui.packed') }}
          </span>
          <!-- Consumable: no asset tag or serial -->
          <template v-if="item.item?.item_type !== 4">
            <span v-if="item.asset_tag" class="pick-item__sn text-gray-400">{{ item.asset_tag }}</span>
            <span v-else-if="item.serial_number" class="pick-item__sn text-gray-400">SN: {{ item.serial_number }}</span>
          </template>
        </div>
        <div v-if="item.conflicts?.length" class="pick-item__conflict">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          {{ item.conflicts.map(c => c.project_name).join(', ') }}
        </div>
      </div>

      <!-- Right: status icon + actions -->
      <div class="pick-item__actions" @click.stop>
        <!-- Packed checkmark -->
        <div v-if="isPacked" class="status-icon status-icon--ok">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <!-- Missing badge -->
        <div v-else-if="item.missing" class="status-icon status-icon--missing">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            <circle cx="12" cy="12" r="10"/>
          </svg>
        </div>

        <!-- Action menu (remaining items only) -->
        <template v-else>
          <!-- Type 3 & 4: tap row opens modal (handled by parent via @quantity-tap) -->
          <button
            v-if="item.item?.item_type === 3 || item.item?.item_type === 4"
            class="pick-btn pick-btn--qty"
            @click.stop="$emit('quantity-tap', item)"
            :title="$t('picking.enterQty')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
          </button>

          <!-- Manual check-out button (Type 1 & 2: triggers manual ID entry overlay) -->
          <button
            v-if="item.item?.item_type !== 3 && item.item?.item_type !== 4"
            class="pick-btn pick-btn--manual"
            @click.stop="$emit('manual-checkout', item)"
            :title="$t('picking.manualPack')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>

          <button
            class="pick-btn pick-btn--manual"
            @click.stop="$emit('mark-missing', item)"
            :title="$t('ux.markMissing')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </button>
        </template>

        <!-- Unpack one unit (shown for multi/qty slots that have at least one packed unit) -->
        <button
          v-if="showUnpackBtn"
          class="pick-btn pick-btn--unpack"
          @click.stop="$emit('unpack-one', item)"
          :title="$t('picking.removeOnePacked')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 14l-4-4 4-4"/>
            <path d="M5 10h11a4 4 0 0 1 0 8h-1"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  item: { type: Object, required: true },
})

const emit = defineEmits(['quantity-tap', 'manual-checkout', 'unpack-one', 'mark-missing'])

const typeName = computed(() => {
  const map = { 1: t('itemTypes.unique'), 2: t('itemTypes.multi'), 3: t('itemTypes.quantity'), 4: t('itemTypes.consumable') }
  return map[props.item.item?.item_type] ?? t('itemTypes.unknown')
})

const typeBadgeClass = computed(() => ({
  'badge--unique':      props.item.item?.item_type === 1,
  'badge--multi':       props.item.item?.item_type === 2,
  'badge--qty':         props.item.item?.item_type === 3,
  'badge--consumable':  props.item.item?.item_type === 4,
}))

const checkoutStatusBadge = computed(() => {
  switch (props.item.checkout_status) {
    case 'checked_out':        return { label: t('checkout.checkedOutBadge'),     cls: 'badge--checked-out' }
    case 'partially_returned': return { label: t('picking.statusPartialReturn'),  cls: 'badge--partial-return' }
    case 'returned':           return { label: t('checkoutsOverview.returned'),        cls: 'badge--returned' }
    default:                   return null
  }
})

const showUnpackBtn = computed(() => {
  const t = props.item.item?.item_type
  if (t === 2 || t === 3 || t === 4 || props.item.unresolved)
    return (props.item.packed_quantity ?? 0) > 0
  // Type 1 (Unique): show when packed
  return !!props.item.packed
})

const isPacked = computed(() => {
  const t = props.item.item?.item_type
  if (t === 3 || t === 4)
    return (props.item.packed_quantity ?? 0) > 0 || !!props.item.packed
  if (t === 2 || props.item.unresolved)
    return !!props.item.packed || (props.item.packed_quantity ?? 0) >= (props.item.planned_quantity ?? 1)
  return !!props.item.packed
})

function onRowClick() {
  if (props.item.missing) return
  const t = props.item.item?.item_type
  if (t === 3 || t === 4) {
    emit('quantity-tap', props.item)
  } else {
    if (isPacked.value) return
    emit('manual-checkout', props.item)
  }
}
</script>

<style scoped>
.pick-item {
  display: flex;
  align-items: stretch;
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: .5rem;
  overflow: hidden;
  transition: opacity .2s, border-color .15s;
  cursor: pointer;
  user-select: none;
}

.pick-item--packed { opacity: .55; border-color: #16a34a; }
.pick-item--missing { border-color: #ef4444; }
.pick-item--partial { border-color: #ca8a04; }

.pick-item__stripe {
  width: 4px;
  background: #374151;
  flex-shrink: 0;
}
.pick-item--packed  .pick-item__stripe { background: #16a34a; }
.pick-item--missing .pick-item__stripe { background: #ef4444; }
.pick-item--partial .pick-item__stripe { background: #ca8a04; }

.pick-item__body {
  display: flex;
  align-items: center;
  padding: .75rem 1rem;
  flex: 1;
  gap: .75rem;
}

.pick-item__main { flex: 1; min-width: 0; }

.pick-item__name {
  font-weight: 600;
  color: #f9fafb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pick-item__meta {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin-top: .2rem;
  flex-wrap: wrap;
}

.pick-item__qty-label { font-size: .8rem; color: #9ca3af; }
.pick-item__sn        { font-size: .75rem; }

.pick-item__conflict {
  display: flex;
  align-items: center;
  gap: .3rem;
  font-size: .72rem;
  color: #fbbf24;
  margin-top: .2rem;
}
.pick-item__conflict svg { width: 12px; height: 12px; flex-shrink: 0; }

.badge {
  font-size: .7rem;
  font-weight: 600;
  padding: .1rem .45rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.badge--unique      { background: #1e40af; color: #bfdbfe; }
.badge--multi       { background: #6b21a8; color: #e9d5ff; }
.badge--qty         { background: #065f46; color: #a7f3d0; }
.badge--consumable  { background: #7c2d12; color: #fed7aa; }
.badge--checked-out    { background: #14532d; color: #86efac; }
.badge--partial-return { background: #78350f; color: #fcd34d; }
.badge--returned       { background: #1f2937; color: #6b7280; border: 1px solid #374151; }

.pick-item__actions {
  display: flex;
  align-items: center;
  gap: .375rem;
  flex-shrink: 0;
}

.status-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.status-icon--ok      { background: #166534; color: #86efac; }
.status-icon--missing { background: #7f1d1d; color: #fca5a5; }
.status-icon svg { width: 16px; height: 16px; }

.pick-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background .15s;
}
.pick-btn svg { width: 15px; height: 15px; }

.pick-btn--qty     { background: #065f46; color: #6ee7b7; }
.pick-btn--qty:hover { background: #047857; }

.pick-btn--manual  { background: #1e3a5f; color: #93c5fd; }
.pick-btn--manual:hover { background: #1d4ed8; }

.pick-btn--unpack { background: #3b2e1a; color: #fdba74; }
.pick-btn--unpack:hover { background: #92400e; }
</style>
